import {
  SizeSelection,
  SizeSelection_Size,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {useEffect} from 'react';
import {useSizeIdFromStateOrParams} from '../../ProductUrl/useSizeIdFromStateOrParams';
import {getSizeByIdOrLabel} from '../getSizeByIdOrLabel';
import {useGetFirstAvailableSizeCallback} from '../utils/useGetFirstAvailableSize';
import {useIsSingleSizeSelection} from '../utils/useIsSingleSizeSelection';
import {SetSelectedSize} from './useSelectedSizeState';

const findFirstAvailableSize = (sizes: SizeSelection_Size[] | undefined) => {
  if (sizes) {
    const foundSize = sizes.find(
      item => item.availability?.$case === 'inStock',
    );
    if (foundSize) {
      return foundSize;
    }
  }

  return null;
};

export const usePreselectSize = ({
  filteredSizeId,
  productId,
  recommendedSize,
  setSelectedSize,
  sizeSelection,
  isSizeSelectionOpen,
  selectedSizeId,
}: {
  filteredSizeId?: number;
  productId: number;
  recommendedSize?: SizeSelection_Size;
  setSelectedSize?: SetSelectedSize;
  sizeSelection: SizeSelection;
  isSizeSelectionOpen?: boolean;
  selectedSizeId: number | undefined;
}) => {
  const paramsPreselectedSizeId = useSizeIdFromStateOrParams();

  const isSingleSizeProvided = useIsSingleSizeSelection(sizeSelection.type);
  const getFirstAvailableSizeCallback = useGetFirstAvailableSizeCallback();

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (!setSelectedSize || selectedSizeId) return;

    const type = sizeSelection?.type;
    let timer: ReturnType<typeof setTimeout>;
    let preselectedSize: SizeSelection_Size | null = null;

    const productHasRecommendedSize = Boolean(recommendedSize);

    if (isSingleSizeProvided) {
      preselectedSize = getFirstAvailableSizeCallback(type);
    }

    const preselectedSizeId = filteredSizeId ?? paramsPreselectedSizeId;

    if (preselectedSizeId) {
      const size = getSizeByIdOrLabel({
        sizeSelection,
        sizeIdentifier: {type: 'id', id: String(preselectedSizeId)},
      });

      if (size) {
        preselectedSize = size;
      }
    } else if (productHasRecommendedSize) {
      preselectedSize = recommendedSize!;
    }

    if (!preselectedSize && isSizeSelectionOpen) {
      switch (type?.$case) {
        case 'groupedSizes':
          {
            const sizeGroups = type.groupedSizes.groups;
            if (sizeGroups[0]?.content?.$case === 'sizes') {
              preselectedSize = findFirstAvailableSize(
                sizeGroups[0].content.sizes.sizes,
              );
            }

            if (sizeGroups[0]?.content?.$case === 'sizeRuns') {
              preselectedSize = findFirstAvailableSize(
                sizeGroups[0].content.sizeRuns.sizeRuns[0]?.sizes,
              );
            }
          }
          break;
        case 'inlineSelection':
          preselectedSize = findFirstAvailableSize(type.inlineSelection.sizes);
          break;
        case 'sizeRuns':
          preselectedSize = findFirstAvailableSize(
            type.sizeRuns.sizeRuns[0]?.sizes,
          );
          break;
        case 'sizes':
          preselectedSize = findFirstAvailableSize(type.sizes.sizes);
          break;
      }
    }

    if (preselectedSize && preselectedSize.availability?.$case === 'inStock') {
      // delay the setState call as this would otherwise be overridden by the reset of the default state in `useSelectedSizeState`.
      // This is a quick fix to mitigate an issue in production and is not considered a good solution.
      timer = setTimeout(() => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
        setSelectedSize(preselectedSize!);
      }, 0);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    productId,
    recommendedSize,
    setSelectedSize,
    sizeSelection,
    filteredSizeId,
    isSizeSelectionOpen,
    selectedSizeId,
    paramsPreselectedSizeId,
  ]);
};
